import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Check, Loader2 } from "lucide-react";
import React from "react";
import { toast } from "sonner";

import { ONEC_BASES_QUERY_KEY, selectOneCBase } from "@/api/onec_bases";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toastError } from "@/lib/api-errors";
import { LoadState } from "@/lib/scan-ui/screen";
import { ssoOneCProblem, useOneCBases } from "@/lib/use-onec-bases";
import { cn } from "@/lib/utils";

/** Выбор базы 1С в SSO-сессии: в SSO портал ходит в 1С по JWT в базы
 *  справочника, личные подключения с паролем не используются. Выбор — per-user,
 *  «по умолчанию» — база, отмеченная админом (`PUT /onec-bases/selected`). */
export function SsoBaseCard(): React.ReactElement {
  const qc = useQueryClient();
  const { data, isLoading, error, refetch } = useOneCBases();

  const selectMut = useMutation({
    mutationFn: selectOneCBase,
    onSuccess: (res) => {
      qc.setQueryData(ONEC_BASES_QUERY_KEY, res);
      toast.success("База 1С выбрана");
    },
    onError: toastError("Не удалось выбрать базу 1С"),
  });

  if (isLoading || error || !data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">База 1С</CardTitle>
        </CardHeader>
        <CardContent>
          <LoadState
            isLoading={isLoading}
            error={error}
            onRetry={() => void refetch()}
            className="flex flex-col items-center gap-3 p-4 text-center"
          />
        </CardContent>
      </Card>
    );
  }

  const problem = ssoOneCProblem(data);
  const busy = selectMut.isPending;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">База 1С</CardTitle>
        <p className="text-sm text-muted-foreground">
          В 1С вы работаете под своей учётной записью по токену SSO — выберите, в какую базу.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {problem && (
          <div
            role="alert"
            className="flex gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
          >
            <AlertTriangle className="mt-0.5 size-4 shrink-0" />
            <div>
              <div className="font-semibold">Не настроен доступ к 1С</div>
              <div>{problem}</div>
            </div>
          </div>
        )}
        {data.bases.length > 0 && (
          <ul className="space-y-2" aria-label="Базы 1С">
            {data.bases.map((b) => {
              const active = b.id === data.effective_id;
              return (
                <li key={b.id}>
                  <button
                    type="button"
                    disabled={busy || b.id === data.selected_id}
                    aria-pressed={active}
                    onClick={() => selectMut.mutate(b.id)}
                    className={cn(
                      "flex w-full items-center justify-between gap-3 rounded-md border p-3 text-left",
                      active ? "border-primary bg-accent" : "hover:bg-accent/50",
                    )}
                  >
                    <span className="min-w-0">
                      <span className="flex items-center gap-2 font-medium">
                        {b.name}
                        {b.is_default && (
                          <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                            по умолчанию
                          </span>
                        )}
                      </span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {b.base_url}
                      </span>
                    </span>
                    {active && <Check className="size-5 shrink-0 text-primary" />}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        {data.selected_id !== null && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={() => selectMut.mutate(null)}
          >
            {busy && <Loader2 className="size-4 animate-spin" />}
            Использовать базу по умолчанию
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
